// AssetContextMenu — right-click menu for asset cards.
//
// Position-fixed at the click coords, closes on outside click or Escape.
// Items are kind-aware: every asset gets Copy ID / Delete; testable kinds
// (audio, body/face anim, prefab) also get Test; materials add an
// "Apply to ▸" submenu listing every mesh slot from the active rig's
// material_mapping.

import React, { useEffect, useRef, useState } from 'react'
import { isRenameableAsset } from '../../lib/renameAsset'
import { blueprintGraphGlyph, blueprintGraphLabel } from '../../lib/assetKindColors'
import { useOverlayAnchor } from '../../lib/useOverlayAnchor'
import { Z } from '../../lib/zLayers'
import MenuSubmenu, { isInsideSubmenu } from '../MenuSubmenu'

const C = {
  bg:       '#1a1d28',
  bgHover:  '#252938',
  border:   '#2a2c36',
  text:     '#e1e1ed',
  dim:      '#8f8fa2',
  danger:   '#f87171',
  accent:   '#bec2ff',
}

export default function AssetContextMenu({
  asset, x, y, onClose,
  onTest, onEdit, onCopyId, onDelete, onRename,
  // blueprint-only: open this row's graph in its editor, or export it to a file.
  onOpenBlueprintGraph, onExportBlueprint,
  // version control:
  onItemHistory,
  // material-only:
  meshSlots, onApplyToSlot,
  // environment-only:
  onApply,
}) {
  const [submenuOpen, setSubmenuOpen] = useState(false)
  const W = 220

  // Position, clamp and feedback all come from useOverlayAnchor — the shared
  // implementation. This menu is where the two bugs it exists for were found:
  // a hand-summed ESTIMATED_H that rotted every time an item was added (Item
  // History, Rename, the Apply-to submenu), and a clamp that mixed the event's
  // viewport space with the menu's own zoomed space. Both are its problem now,
  // for every menu at once.
  //
  // Items here mount ASYNCHRONOUSLY (Test gates on the asset kind resolving,
  // the material submenu on meshSlots) — measured, this menu is 127px at first
  // layout and 180px settled — which is exactly why the hook re-clamps on size
  // change rather than measuring once.
  const { ref, props: anchor } = useOverlayAnchor(x, y, { margin: 8 })

  useEffect(() => {
    const onDoc = (e) => {
      // The submenu is portaled to <body> (MenuSubmenu), so it is NOT inside
      // ref.current. Without this a mousedown on a submenu row reads as an
      // outside click and unmounts the menu before the click reaches the row —
      // the item would simply never fire.
      if (isInsideSubmenu(e.target)) return
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const testable = ['audio', 'body_anim', 'face_anim', 'prefab'].includes(asset.kind)
  const isMaterial = asset.kind === 'material'
  const isEnvironment = asset.kind === 'environment'

  return (
    <div
      {...anchor}
      style={{
        ...anchor.style, width: W, zIndex: Z.CONTEXT_MENU,
        background: C.bg, border: `1px solid ${C.border}`,
        borderRadius: 4, padding: 4,
        boxShadow: '0 8px 24px rgba(0,0,0,.5)',
        fontFamily: 'var(--m-font)', fontSize: 11, color: C.text,
      }}
    >
      <Header>{asset.name}</Header>
      <Divider />
      {testable && (
        <Item icon="▶" label="Test" onClick={() => { onTest?.(asset); onClose() }} />
      )}
      {asset.kind === 'blueprint_asset' && asset.graphKind && onOpenBlueprintGraph && (
        <Item
          icon={blueprintGraphGlyph(asset.graphKind) ?? '⌁'}
          label={`Open ${blueprintGraphLabel(asset.graphKind)} editor`}
          onClick={() => { onOpenBlueprintGraph(asset); onClose() }}
        />
      )}
      {/* Export ONE card's graph. The unit is the card, not the character: a
          blueprint_asset row carries exactly one graph (ENG-1030), so a file
          does too. Re-imports as a NEW card, never over an existing one. */}
      {asset.kind === 'blueprint_asset' && asset.graphKind && onExportBlueprint && (
        <Item
          icon="↓"
          label="Export to file…"
          onClick={() => { onExportBlueprint(asset); onClose() }}
        />
      )}
      {asset.kind === 'prefab' && onEdit && (
        <Item icon="⚙" label="Edit" onClick={() => { onEdit(asset); onClose() }} />
      )}
      {isEnvironment && (
        <Item icon="🌅" label="Apply environment" onClick={() => { onApply?.(asset); onClose() }} />
      )}
      {onRename && isRenameableAsset(asset) && (
        <Item icon="✎" label="Rename" hint="F2" onClick={() => { onRename(asset); onClose() }} />
      )}
      <Item icon="⎘" label="Copy ID" onClick={() => { onCopyId?.(asset); onClose() }} />
      {isMaterial && meshSlots && meshSlots.length > 0 && (
        <SubmenuItem
          icon="↳" label="Apply to ▸"
          open={submenuOpen}
          onMouseEnter={() => setSubmenuOpen(true)}
          onMouseLeave={() => setSubmenuOpen(false)}
        >
          {meshSlots.map((slot, i) => (
            <Item
              key={`${slot.meshIndex}_${i}`}
              label={`Element ${i} — ${slot.meshName ?? '(unnamed)'}`}
              onClick={() => { onApplyToSlot?.(asset, slot); onClose() }}
            />
          ))}
        </SubmenuItem>
      )}
      {onItemHistory && (
        <Item icon="⎇" label="Item History" onClick={() => { onItemHistory(asset); onClose() }} />
      )}
      <Divider />
      <Item icon="🗑" label="Delete" danger onClick={() => { onDelete?.(asset); onClose() }} />
    </div>
  )
}

function Header({ children }) {
  return (
    <div style={{
      padding: '4px 8px 6px', color: C.dim,
      letterSpacing: '.05em', overflow: 'hidden',
      textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }} title={children}>{children}</div>
  )
}

function Divider() {
  return <div style={{ height: 1, background: C.border, margin: '2px 0' }} />
}

function Item({ icon, label, hint, onClick, danger }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', textAlign: 'left',
        background: hover ? C.bgHover : 'transparent',
        border: 'none', borderRadius: 3,
        padding: '5px 8px',
        color: danger ? C.danger : C.text,
        fontFamily: 'var(--m-font)', fontSize: 11,
        cursor: 'pointer',
      }}
    >
      <span style={{ width: 14, textAlign: 'center', color: C.dim }}>{icon ?? ''}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {hint && <span style={{ color: C.dim, fontSize: 10 }}>{hint}</span>}
    </button>
  )
}

function SubmenuItem({ icon, label, open, onMouseEnter, onMouseLeave, children }) {
  const rowRef = useRef(null)
  return (
    <div
      ref={rowRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ position: 'relative' }}
    >
      <button
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', textAlign: 'left',
          background: open ? C.bgHover : 'transparent',
          border: 'none', borderRadius: 3,
          padding: '5px 8px',
          color: C.text, fontFamily: 'var(--m-font)', fontSize: 11,
          cursor: 'default',
        }}
      >
        <span style={{ width: 14, textAlign: 'center', color: C.dim }}>{icon ?? ''}</span>
        <span>{label}</span>
      </button>
      <MenuSubmenu
        rowRef={rowRef}
        open={open}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: 4, padding: 4,
          boxShadow: '0 8px 24px rgba(0,0,0,.5)',
          fontFamily: 'var(--m-font)', fontSize: 11, color: C.text,
        }}
      >
        {children}
      </MenuSubmenu>
    </div>
  )
}
